import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useClientPermissions = () => {
  const { user } = useAuth();
  
  const isClient = user?.role === 'client';
  
  const allowedRoutes = isClient 
    ? ['/dashboard'] // Clients can only access dashboard
    : ['/dashboard', '/projects', '/leads', '/suppliers', '/team', '/candidates']; // All other roles get full access
  
  const canAccess = (route: string) => {
    if (!isClient) return true; // Non-clients have full access
    return allowedRoutes.includes(route);
  };

  // Check if client has access to a specific project
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['client-accessible-projects', user?.id],
    queryFn: async () => {
      if (!user?.id || !isClient) return [];
      
      const { data, error } = await supabase
        .from('client_project_access')
        .select(`
          project_id,
          team_members!client_project_access_team_member_id_fkey (
            user_id
          )
        `)
        .eq('is_active', true)
        .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
        .eq('team_members.user_id', user.id);
      
      if (error) {
        console.error('Error fetching accessible projects:', error);
        return [];
      }
      
      return data?.map(item => item.project_id) || [];
    },
    enabled: isClient && !!user?.id,
  });

  const canAccessProject = (projectId: string) => {
    if (!isClient) return true; // Non-clients have full access
    return accessibleProjects.includes(projectId);
  };
  
  return {
    isClient,
    allowedRoutes,
    canAccess,
    canAccessProject,
    accessibleProjects
  };
};